import { leadPlacement, PIPELINE_ACTIVE_PHASES } from './lead-location.js';
const ISO = /^\d{4}-\d{2}-\d{2}(?:[T ][\d:.]+(?:Z|[+-]\d{2}(?::?\d{2})?)?)?$/;
/** base64url of ASCII-only JSON — `btoa` exists in the browser and in Node, so the mock and the API share it. */
const ascii = (s) => s.replace(/[\u0080-\uffff]/g, (c) => `\\u${c.charCodeAt(0).toString(16).padStart(4, '0')}`);
export function encodeCursor(cursor) {
    return btoa(ascii(JSON.stringify([cursor.at, cursor.id]))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
export function decodeCursor(raw) {
    if (raw.length > 400)
        return null;
    try {
        if (!/^[A-Za-z0-9_-]+$/.test(raw))
            return null;
        const value = JSON.parse(atob(raw.replace(/-/g, '+').replace(/_/g, '/')));
        if (!Array.isArray(value) || value.length !== 2)
            return null;
        const [at, id] = value;
        if (typeof id !== 'string' || !id || id.length > 200)
            return null;
        // '' is how the first pager (#599) wrote an undated row; it means the same as null.
        if (at !== null && at !== '' && (typeof at !== 'string' || !ISO.test(at) || !Number.isFinite(Date.parse(at))))
            return null;
        return { at: at === '' ? null : at, id };
    }
    catch {
        return null;
    }
}
/**
 * `page_size` (1–200) switches a route to paging; absent, the route answers the whole list, newest first, exactly as
 * before. The name is #599's (the iOS app), and it is deliberately NOT `limit`: older callers send `limit` to routes
 * that never applied it (`/api/contacts?limit=50` returned everyone) and must keep getting the whole list, not a page
 * whose cursor they would ignore. A cursor that does not decode is refused either way.
 */
export function readPageParams(query) {
    const cursor = query.cursor ? decodeCursor(query.cursor) : null;
    if (query.cursor && !cursor)
        return 'invalid';
    if (query.page_size === undefined)
        return null;
    const n = Number(query.page_size);
    if (!Number.isInteger(n) || n < 1 || n > 200)
        return 'invalid';
    return { limit: n, cursor };
}
const time = (at) => {
    const t = typeof at === 'string' && at ? Date.parse(at) : NaN;
    return Number.isFinite(t) ? t : -Infinity;
};
const text = (v) => (v === null || v === undefined ? '' : String(v));
/** Date descending; an undated row sorts last; the id breaks ties, descending, so the order is total. */
export function compareNewest(a, b) {
    const d = time(b.at) - time(a.at);
    if (d > 0)
        return 1;
    if (d < 0)
        return -1;
    const ia = text(a.id), ib = text(b.id);
    return ib < ia ? -1 : ib > ia ? 1 : 0;
}
export function newestFirst(rows, at, id) {
    return rows.map((row) => ({ row, key: { at: at(row), id: id(row) } }))
        .sort((a, b) => compareNewest(a.key, b.key)).map((x) => x.row);
}
/** One page of an already-sorted list. `total` is the whole filtered list, never the page. */
export function pageRows(sorted, at, id, page) {
    const start = page.cursor ? sorted.findIndex((row) => compareNewest({ at: at(row), id: id(row) }, page.cursor) > 0) : 0;
    const from = start < 0 ? sorted.length : start;
    const data = sorted.slice(from, from + page.limit);
    const last = data.at(-1);
    const more = from + data.length < sorted.length && last !== undefined;
    const lastAt = last === undefined ? null : at(last);
    return {
        data,
        next_cursor: more ? encodeCursor({ at: typeof lastAt === 'string' && lastAt ? lastAt : null, id: text(id(last)) }) : null,
        total: sorted.length,
    };
}
export function listBody(sorted, at, id, page, extra = {}) {
    if (!page)
        return { ok: true, data: sorted, ...extra };
    const p = pageRows(sorted, at, id, page);
    return { ok: true, data: p.data, next_cursor: p.next_cursor, total: p.total, ...extra };
}
const has = (q, ...values) => !q || values.some(v => typeof v === 'string' && v.toLowerCase().includes(q.toLowerCase()));
const tally = (keys, rows, belongs) => Object.fromEntries(keys.map(k => [k, rows.filter(r => belongs(r, k)).length]));
// ── Pipeline ──────────────────────────────────────────────────────────────
/** 'All' is every ACTIVE phase. Stalled is parked work: its own filter, never in the default view. */
export const PIPELINE_FILTERS = ['All', 'Enquiry', 'Preparing quote', 'Quote sent', 'Accepted — create Job', 'Stalled'];
/** `active` is #599's name for 'All' (the iOS app); both mean every phase but Stalled. */
export const isPipelineFilter = (filter) => filter === 'active' || PIPELINE_FILTERS.includes(filter);
export const inPipelineFilter = (filter, phase) => filter === 'All' || filter === 'active' ? PIPELINE_ACTIVE_PHASES.includes(phase) : phase === filter;
export function pipelineList(rows, query, page) {
    const phase = query.phase && isPipelineFilter(query.phase) ? query.phase : null;
    const searched = newestFirst(rows, r => r.updated_at, r => r.id).filter(r => has(query.q, r.name, r.suburb));
    const shown = phase ? searched.filter(r => inPipelineFilter(phase, r.phase)) : searched;
    if (!phase && !query.q && !page)
        return listBody(searched, r => r.updated_at, r => r.id, null);
    return listBody(shown, r => r.updated_at, r => r.id, page, { counts: tally([...PIPELINE_FILTERS], searched, (r, f) => inPipelineFilter(f, r.phase)), value_total: shown.reduce((n, r) => n + (Number(r.value) || 0), 0) });
}
// ── Job register ──────────────────────────────────────────────────────────
/** A job's date is when it was created (the Age column), never its ref: J-MMYY-NNNNN does not sort by date. */
export const jobDate = (j) => j.creation_date ?? j.created_at;
export function jobRegisterList(rows, query, page) {
    const sorted = newestFirst(rows, jobDate, j => j.ref);
    if (!query.group && !query.stage && !query.q && !page)
        return listBody(sorted, jobDate, j => j.ref, null);
    const group = query.group ?? 'jobs';
    const searched = sorted.filter(j => has(query.q, j.name, j.ref, j.suburb));
    const inGroup = searched.filter(j => j.register_group === group);
    const shown = query.stage ? inGroup.filter(j => (j.effective_stage ?? 'No stage') === query.stage) : inGroup;
    const stages = {};
    for (const j of inGroup) {
        const s = String(j.effective_stage ?? 'No stage');
        const x = stages[s] ??= { n: 0, total: 0 };
        x.n++;
        x.total += Number(j.deal_value) || 0;
    }
    return listBody(shown, jobDate, j => j.ref, page, { counts: tally(['jobs', 'history', 'review'], searched, (j, g) => j.register_group === g), stages });
}
// ── Lead history ──────────────────────────────────────────────────────────
/** The Lead inbox tabs, decided where the whole history is. `mine` is the caller's first name, as the owner is recorded. */
export const LEAD_TABS = ['accepted', 'mine', 'all', 'closed'];
export const leadOwnerName = (l) => (l.channel_signals?.response?.owner_name) ?? l.owner;
export function inLeadTab(l, tab, owner, now = Date.now()) {
    if (tab === 'all')
        return true;
    if (tab === 'accepted')
        return leadPlacement(l) === 'active';
    if (tab === 'mine')
        return !!owner && leadOwnerName(l) === owner && leadPlacement(l) === 'active';
    if (tab === 'closed')
        return ['won', 'lost', 'dead', 'completed'].includes(String(l.outcome ?? '')) && Date.parse(String(l.updated_at)) >= now - 7 * 86_400_000;
    return false;
}
export function leadHistoryList(rows, query, page, now = Date.now()) {
    const sorted = newestFirst(rows, l => l.received_at, l => l.id);
    if (!query.tab && !page)
        return listBody(sorted, l => l.received_at, l => l.id, null);
    const filtered = sorted.filter(l => (!query.category || l.category === query.category) && (!query.outcome || l.outcome === query.outcome));
    const tab = query.tab && LEAD_TABS.includes(query.tab) ? query.tab : 'all';
    const categories = [...new Set(sorted.map(l => l.category).filter((c) => typeof c === 'string' && !!c))].sort();
    return listBody(filtered.filter(l => inLeadTab(l, tab, query.owner, now)), l => l.received_at, l => l.id, page, { counts: tally([...LEAD_TABS], filtered, (l, t) => inLeadTab(l, t, query.owner, now)), categories });
}
// ── Customers ─────────────────────────────────────────────────────────────
export const CUSTOMER_VIEWS = ['current', 'leads', 'pipeline', 'jobs', 'past', 'unlinked', 'all'];
export const inCustomerView = (c, view) => view === 'all' || c.customer_view === view || (Array.isArray(c.work_groups) && c.work_groups.includes(view));
export function customerList(rows, query, page) {
    const sorted = newestFirst(rows, c => c.updated_at, c => c.id);
    if (!query.view && !page)
        return listBody(sorted, c => c.updated_at, c => c.id, null);
    const view = query.view && CUSTOMER_VIEWS.includes(query.view) ? query.view : 'all';
    return listBody(sorted.filter(c => inCustomerView(c, view)), c => c.updated_at, c => c.id, page, { counts: tally([...CUSTOMER_VIEWS], sorted, inCustomerView) });
}
//# sourceMappingURL=crm-list-page.js.map